import { Control, Controller, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  Switch,
  TextField,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { EditModeFormValues } from '../editModeFormType';
import { EnumOptionsEditor } from './EnumOptionsEditor';

type EditValueRowProps = {
  control: Control<EditModeFormValues>;
  index: number;
  onRemove: () => void;
};

export const EditValueRow = ({
  control,
  index,
  onRemove,
}: EditValueRowProps) => {
  const intl = useIntl();

  const dataType = useWatch({
    control,
    name: `uiEditValues.${index}.dataType`,
  });

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: borderRadius('m'),
      }}
      data-cy="edit-mode-edit-value-row"
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Controller
          control={control}
          name={`uiEditValues.${index}.label`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={intl.formatMessage({
                id: 'features.queueSettings.editMode.editValues.label',
              })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ flex: 1 }}
            />
          )}
        />
        <Controller
          control={control}
          name={`uiEditValues.${index}.id`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={intl.formatMessage({
                id: 'features.queueSettings.editMode.editValues.id',
              })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ flex: 1 }}
            />
          )}
        />
        <Controller
          control={control}
          name={`uiEditValues.${index}.dataType`}
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label={intl.formatMessage({
                id: 'features.queueSettings.editMode.editValues.dataType',
              })}
              sx={{ width: 160 }}
            >
              <MenuItem value="string">
                {intl.formatMessage({
                  id: 'features.queueSettings.editMode.editValues.dataType.string',
                })}
              </MenuItem>
              <MenuItem value="enum">
                {intl.formatMessage({
                  id: 'features.queueSettings.editMode.editValues.dataType.enum',
                })}
              </MenuItem>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name={`uiEditValues.${index}.required`}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                />
              }
              label={intl.formatMessage({
                id: 'features.queueSettings.editMode.editValues.required',
              })}
              sx={{ mt: 0.5 }}
            />
          )}
        />
        <IconButton
          onClick={onRemove}
          data-cy="edit-mode-remove-edit-value"
          sx={{ mt: 0.5 }}
        >
          <SvgIcon fontSize="small">
            <IconTrash />
          </SvgIcon>
        </IconButton>
      </Stack>
      {dataType === 'enum' && (
        <>
          <Divider />
          <EnumOptionsEditor control={control} editValueIndex={index} />
        </>
      )}
    </Stack>
  );
};
